import { useEffect, RefObject } from 'react';

type Event = MouseEvent | TouchEvent;

/**
 * A hook to detect clicks outside a specified element.
 * @param ref - A ref object pointing to the element to monitor.
 * @param handler - The function to call when a click outside is detected.
 * @param preventCloseOnOutsideClick - If true, prevents the handler from being called on outside clicks.
 */
export const useClickOutside = <T extends HTMLElement = HTMLElement>(
  ref: RefObject<T>,
  handler: (event: Event) => void,
  preventCloseOnOutsideClick?: boolean
) => {
  useEffect(() => {
    const listener = (event: Event) => {
      const el = ref.current;
      // Do nothing if the click is on the element or its descendants
      if (!el || el.contains(event.target as Node)) {
        return;
      }
      // If preventCloseOnOutsideClick is true, don't call the handler
      if (preventCloseOnOutsideClick) {
        return;
      }
      handler(event);
    };

    // Use mousedown to capture the event before a blur event on an input can occur.
    document.addEventListener('mousedown', listener);
    document.addEventListener('touchstart', listener);

    return () => {
      document.removeEventListener('mousedown', listener);
      document.removeEventListener('touchstart', listener);
    };
  }, [ref, handler, preventCloseOnOutsideClick]); // Re-run if ref, handler, or preventCloseOnOutsideClick changes
};
